import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as MarketplaceActions from 'app/main/marketplace/store/actions/marketplace.actions';

/**
 * ShortcodeParser Component
 * Parses WordPress-style shortcodes in custom HTML for campaigns
 * Supported shortcodes:
 * - [launch app_id="<appId>"] - Creates a button/link that launches an app with SSO
 */
const ShortcodeParser = ({ htmlContent }) => {
	const containerRef = useRef(null);
	const dispatch = useDispatch();
	const userSession = useSelector(({ auth }) => auth.user);
	const [apps, setApps] = useState([]);

	// Fetch apps from marketplace JSON file
	useEffect(() => {
		const url = `${window.location.origin}/assets/marketplace-data/marketplaceData.json?t=${new Date().getTime()}`;
		fetch(url)
			.then(response => response.json())
			.then(data => {
				setApps(data || []);
			})
			.catch(error => {
				console.error('Failed to load marketplace data:', error);
			});
	}, []);

	useEffect(() => {
		if (!containerRef.current || !htmlContent) return;

		// Parse and replace shortcodes
		const parsedHtml = parseShortcodes(htmlContent);
		containerRef.current.innerHTML = parsedHtml;

		// Attach event listeners to shortcode-generated elements
		attachShortcodeHandlers();
	}, [htmlContent, apps, userSession]);

	const parseShortcodes = (html) => {
		// Replace [launch app_id="xxx"] shortcodes
		return html.replace(/\[launch\s+app_id=["']([^"']+)["']\]/g, (match, appId) => {
			return createLaunchButton(appId);
		});
	};

	const createLaunchButton = (appId) => {
		const app = apps?.find(a => (a.id || a._id) === appId);
		
		if (!app) {
			console.warn(`App with ID "${appId}" not found`);
			return `<span style="color: red;">App not found: ${appId}</span>`;
		}

		// Generate button matching the heroic-button style from custom HTML
		return `<button 
			class="heidi-shortcode-launch heroic-button" 
			data-app-id="${appId}"
			data-app-name="${app.name || ''}"
		>
			Start Playing Now!
			<span style="font-size: 1.8rem;">🎮</span>
		</button>`;
	};

	const attachShortcodeHandlers = () => {
		if (!containerRef.current) return;

		// Find all shortcode-generated launch buttons
		const launchButtons = containerRef.current.querySelectorAll('.heidi-shortcode-launch');
		
		launchButtons.forEach(button => {
			button.addEventListener('click', handleLaunchClick);
		});
	};

	const handleLaunchClick = async (event) => {
		event.preventDefault();
		event.stopPropagation();
		
		// Get the button element (might be clicking on the span inside)
		const button = event.target.closest('.heidi-shortcode-launch');
		if (!button) return;
		
		const appId = button.getAttribute('data-app-id');
		const appData = apps?.find(a => (a.id || a._id) === appId);

		if (!appData) {
			console.error('App not found for launch:', appId);
			return;
		}


		try {
			// Handle web platform apps
			if (appData.platform === 'web' && appData.config?.web_app_url) {
				let launchUrl = appData.config.web_app_url;

				// Add SSO token for apps that have SSO enabled
				if (appData.config?.sso_enabled && userSession) {
					try {
						const ssoToken = await dispatch(MarketplaceActions.generateMarketplaceSSO(appData.config));
						const separator = launchUrl.includes('?') ? '&' : '?';
						launchUrl = `${launchUrl}${separator}heidi_token=${ssoToken}`;
					} catch (error) {
						console.error('Failed to generate SSO token:', error);
						// Open without SSO token as fallback
					}
				} 

				window.open(launchUrl, '_blank');
				return;
			}

			// Handle other platform types as needed
			console.warn('Unsupported app platform:', appData.platform);
		} catch (error) {
			console.error('Error launching app:', error);
		}
	};

	// Cleanup event listeners on unmount
	useEffect(() => {
		return () => {
			if (containerRef.current) {
				const buttons = containerRef.current.querySelectorAll('.heidi-shortcode-launch');
				buttons.forEach(button => {
					button.removeEventListener('click', handleLaunchClick);
				});
			}
		};
	}, []);

	return <div ref={containerRef} />;
};

export default ShortcodeParser;
